(() => {
  'use strict';

  const drawer = document.querySelector('cart-drawer');
  if (!drawer || drawer.dataset.jvUpsellBound) return;
  drawer.dataset.jvUpsellBound = 'true';

  const tiers = [[7900, 6], [11900, 12], [15900, 18], [22900, 30], [29900, 40]];
  const lookups = new Map();
  const products = new Map();
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let placeholder = null;
  let fillToken = 0;
  let previousBar = null;
  let previousPct = null;
  let adding = false;

  const normalize = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

  function parseTitle(title) {
    const match = normalize(title).match(/^(.+) print (long sleeve camp collar shirt|casual trousers with elastic waistband)$/);
    return match ? { print: match[1], garment: match[2] } : null;
  }

  async function getJSON(path) {
    const response = await fetch(root + path, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Lookup failed');
    return response.json();
  }

  // Cache source queries independently from cart exclusions: after an add, the
  // same source can choose another result without repeating either query.
  function sourceLookup(data) {
    const key = String(data.productId);
    if (!lookups.has(key)) lookups.set(key, {});
    return lookups.get(key);
  }

  function details(candidate) {
    const key = String(candidate.id);
    if (!products.has(key)) {
      const request = getJSON(`products/${encodeURIComponent(candidate.handle)}.js`);
      products.set(key, request);
      request.catch(() => products.delete(key));
    }
    return products.get(key);
  }

  async function firstAvailable(candidates, excluded, matchTitle) {
    for (const candidate of candidates) {
      if (!candidate.available || !candidate.handle || excluded.has(String(candidate.id))) continue;
      if (matchTitle && !matchTitle(candidate.title)) continue;
      try {
        const product = await details(candidate);
        if (excluded.has(String(product.id)) || !product.available || product.requires_selling_plan) continue;
        if (matchTitle && !matchTitle(product.title)) continue;
        if (product.variants?.some((variant) => variant.available && variant.id)) return product;
      } catch (_) {
        // One unavailable product endpoint must not prevent the fallback.
      }
    }
    return null;
  }

  async function chooseProduct(data) {
    const entry = sourceLookup(data);
    const source = parseTitle(data.title);
    const excluded = new Set(data.productIds.map(String));
    if (source) {
      const complements = (title) => {
        const parsed = parseTitle(title);
        return parsed && parsed.print === source.print && parsed.garment !== source.garment;
      };
      if (!entry.match) {
        const query = new URLSearchParams({
          q: source.print,
          'resources[type]': 'product',
          'resources[limit]': '10',
          'resources[options][unavailable_products]': 'hide',
          'resources[options][fields]': 'title',
        });
        entry.match = getJSON(`search/suggest.json?${query}`)
          .then((result) => result.resources?.results?.products || [])
          .catch(() => { entry.match = null; return []; });
      }
      const product = await firstAvailable(await entry.match, excluded, complements);
      if (product) return { product, kicker: 'Complete the set' };
    }
    if (!entry.related) {
      entry.related = getJSON(`recommendations/products.json?product_id=${encodeURIComponent(data.productId)}&limit=6&intent=related`)
        .then((result) => result.products || [])
        .catch(() => { entry.related = null; return []; });
    }
    const product = await firstAvailable(await entry.related, excluded);
    return product ? { product, kicker: 'Pair it with' } : null;
  }

  function selectVariant(product, sourceOptions) {
    const available = product.variants.filter((variant) => variant.available && variant.id);
    const sourceSize = sourceOptions.find((option) => /size/i.test(option.name));
    const sizeIndex = product.options.findIndex((option) => /size/i.test(typeof option === 'string' ? option : option.name));
    const matching = sourceSize && sizeIndex >= 0
      ? available.find((variant) => normalize(variant.options[sizeIndex]) === normalize(sourceSize.value))
      : null;
    return { available, selected: matching || available[0] };
  }

  function whyText(subtotal, price) {
    const current = tiers.filter(([min]) => subtotal >= min).pop();
    const after = tiers.filter(([min]) => subtotal + price >= min).pop();
    const next = tiers.find(([min]) => subtotal < min);
    return after && (!current || after[1] > current[1])
      ? `Adding this unlocks $${after[1]} off`
      : next ? `Gets you closer to $${next[1]} off` : '';
  }

  function money(cents, format) {
    if (typeof window.Shopify?.formatMoney === 'function') {
      // Store money formats can contain a span; return text for safe DOM insertion.
      const template = document.createElement('template');
      template.innerHTML = window.Shopify.formatMoney(cents, format);
      return template.content.textContent;
    }
    return `$${(Number(cents) / 100).toFixed(2)}`;
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function isCurrent(row, token) {
    return token === fillToken && row.isConnected && drawer.querySelector('[data-jv-upsell]') === row;
  }

  function renderRow(row, data, result, token) {
    const { product, kicker } = result;
    const { available, selected } = selectVariant(product, data.options);
    if (!selected) return;
    let variant = selected;
    const image = element('img', 'jv-cart-upsell__image');
    image.width = 56;
    image.height = 70;
    image.alt = product.title;
    const imageSource = product.featured_image || product.images?.[0];
    if (imageSource) image.src = typeof imageSource === 'string' ? imageSource : imageSource.src;
    else image.hidden = true;
    const body = element('div', 'jv-cart-upsell__body');
    const title = element('p', 'jv-cart-upsell__title');
    const shortTitle = product.title.replace(/\s+Print\s+Casual Trousers.*$/i, ' Trousers').replace(/\s+Print\s+Long Sleeve Camp Collar Shirt.*$/i, ' Shirt');
    const link = element('a', '', shortTitle);
    link.href = `${root}products/${encodeURIComponent(product.handle)}`;
    const price = element('span', '');
    title.append(link, price);
    const why = element('p', 'jv-cart-upsell__why');
    const controls = element('div', 'jv-cart-upsell__controls');
    const select = element('select', '');
    select.setAttribute('aria-label', `Size for ${product.title}`);
    available.forEach((item) => {
      const option = element('option', '', item.title);
      option.value = String(item.id);
      select.append(option);
    });
    select.value = String(variant.id);
    if (product.variants.length > 1) controls.append(select);
    const update = () => {
      price.textContent = ` · ${money(variant.price, data.moneyFormat)}`;
      why.textContent = whyText(data.subtotal, Number(variant.price));
    };
    select.addEventListener('change', () => {
      variant = available.find((item) => String(item.id) === select.value) || selected;
      update();
    });
    const button = element('button', '', 'Add');
    button.type = 'button';
    button.disabled = adding;
    const error = element('p', 'jv-cart-upsell__error');
    error.setAttribute('role', 'status');
    button.addEventListener('click', async () => {
      if (adding || !variant?.id || !isCurrent(row, token)) return;
      adding = true;
      button.disabled = true;
      select.disabled = true;
      button.textContent = 'Adding…';
      error.textContent = '';
      let added = false;
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: variant.id, quantity: 1,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error('Add failed');
        added = true;
        // A cart quantity update may have replaced this row while the POST ran.
        if (!isCurrent(row, token)) return;
        const parser = new DOMParser();
        if (!state.id || typeof drawer.renderContents !== 'function' ||
          !state.sections?.['cart-drawer'] || !state.sections?.['cart-icon-bubble'] ||
          !parser.parseFromString(state.sections['cart-drawer'], 'text/html').querySelector('#CartDrawer') ||
          !parser.parseFromString(state.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section') ||
          !document.getElementById('cart-icon-bubble')) throw new Error('Refresh failed');
        drawer.renderContents(state);
        // Already-open drawers may not emit another transitionend. Rebind the
        // focus trap to the new DOM instead of leaving it on removed controls.
        requestAnimationFrame(() => {
          if (drawer.classList.contains('active') && typeof trapFocus === 'function') {
            trapFocus(drawer.querySelector('#CartDrawer'), drawer.querySelector('.drawer__inner'));
          }
        });
      } catch (_) {
        if (isCurrent(row, token)) {
          error.textContent = added ? 'Added. Refresh the cart to see your item.' : 'Couldn’t add this item. Please try again.';
        }
      } finally {
        adding = false;
        if (isCurrent(row, token)) {
          // Do not offer a second add when only section rendering failed.
          button.disabled = added;
          select.disabled = added;
          button.textContent = added ? 'Added' : 'Add';
        }
        const currentButton = drawer.querySelector('[data-jv-upsell] button');
        if (currentButton && currentButton !== button) currentButton.disabled = false;
      }
    });
    controls.append(button);
    body.append(element('p', 'jv-cart-upsell__kicker', kicker), title, why, controls, error);
    update();
    row.append(image, body);
    row.hidden = false;
  }

  function refresh() {
    const bar = drawer.querySelector('.jv-cart-progress__fill');
    if (bar !== previousBar) {
      if (bar) {
        const pct = Math.max(0, Math.min(100, Number(bar.dataset.pct)));
        if (previousPct !== null && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          bar.style.width = `${previousPct}%`;
          // Commit the old width before applying the new target.
          void bar.offsetWidth;
          bar.style.transition = 'width .45s ease';
          bar.style.width = `${pct}%`;
        }
        previousPct = pct;
      } else previousPct = null;
      previousBar = bar;
    }
    const row = drawer.querySelector('[data-jv-upsell]');
    // Identity, not innerHTML, is the guard: our own row mutations do no work.
    if (row === placeholder) return;
    placeholder = row;
    const token = ++fillToken;
    if (!row) return;
    try {
      const data = JSON.parse(row.querySelector('[data-jv-upsell-data]').textContent);
      chooseProduct(data).then((result) => {
        if (result && isCurrent(row, token)) renderRow(row, data, result, token);
      }).catch(() => { /* No recommendation is a valid empty state. */ });
    } catch (_) { /* Malformed or absent data leaves the placeholder hidden. */ }
  }

  new MutationObserver(refresh).observe(drawer, { childList: true, subtree: true });
  refresh();
})();
